import uuid from 'react-uuid';
import { createSlice } from '@reduxjs/toolkit';

import { relativesRoles, status } from 'utils/const';
import { dancerDefaultValuesModel } from 'models/dancerDefaultValuesModel';
import { relativeEditModel } from 'models/relativeEditModel';
import { createdRelativeResponseModel } from 'models/createdRelativeResponseModel';
import { getPerformanceDateTime } from 'utils/dates';
import { thunks } from './thunks';
import { selectors } from './selectors';
import { mediaTypes } from '../../utils/media';
import { calculateFiltersCount } from '../media';

const defaultMediaFilterValues = {
  type: mediaTypes.BOTH,
  dancerSearch: { value: '', label: '' },
  dancers: [],
  classSearch: { value: '', label: '' },
  classes: [],
  eventSearch: { value: '', label: '' },
  events: [],
  routineSearch: { value: '', label: '' },
  routines: [],
  date_start: '',
  date_end: '',
};

const initialState = {
  dancerInfo: {},
  loadingDancerInfoStatus: status.IDLE,
  relationships: [],
  dancerUpcomingAppointments: [],
  loadingAppointmentsStatus: status.IDLE,

  appointmentHistory: {
    list: [],
    loadingStatus: status.IDLE,
    hasMore: true,
    oldestDate: null,
  },

  dancerEvaluation: [],
  ratings: [],
  loadingRatingStatus: status.IDLE,

  notifications: {
    list: [],
    currentPage: 1,
  },
  loadingNotificationsStatus: status.IDLE,

  routines: {
    list: [],
    loadingDancerRoutinesStatus: status.IDLE,
    currentPage: 1,
  },
  performances: {
    dancerPerformances: [],
    currentPage: 1,
    loadingPerformanceStatus: status.IDLE,
  },
  media: {
    list: [],
    filters: {
      fields: defaultMediaFilterValues,
      updatedFields: 0,
    },
    pagination: {
      total: 0,
      uploadedCount: 20,
    },

    loadingMediaListStatus: status.IDLE,
  },

  dancerClasses: [],
  createGuardianStatus: status.IDLE,
  getEvaluationStatus: status.IDLE,
  loadingClassesStatus: status.IDLE,
};

const slice = createSlice({
  name: 'dancerDetails',
  initialState,
  reducers: {
    resetState(state) {
      Object.assign(state, initialState);
    },
    resetStatus(state, { payload }) {
      state[payload] = status.IDLE;
    },
    setPage: (state, { payload }) => {
      state.notifications.currentPage = payload;
    },
    prependNotification: (state, { payload }) => {
      state.notifications.list.unshift(payload);
    },
    addGuardian(state) {
      state.relationships.push({ _id: uuid(), status: 'pending' });
    },
    updateGuardian(state, { payload }) {
      const guardianIndex = state.relationships.findIndex(
        (g) => g._id === payload._id
      );

      if (guardianIndex !== -1) {
        state.relationships[guardianIndex] = {
          ...state.relationships[guardianIndex],
          ...payload.data,
          status: 'saved',
        };
      }
    },
    removeGuardian(state, { payload }) {
      const guardianIndex = state.relationships.findIndex(
        (g) => g._id === payload
      );

      if (guardianIndex !== -1) {
        state.relationships.splice(guardianIndex, 1);
      }
    },
    setRoutinesPage: (state, { payload }) => {
      state.routines.currentPage = payload;
    },
    setPerformancesPage: (state, { payload }) => {
      state.performances.currentPage = payload;
    },
    resetMediaFilters: (state) => {
      state.media.filters = initialState.media.filters;
    },
    setMediaFilters: (state, { payload }) => {
      state.media.filters.updatedFields = calculateFiltersCount(
        defaultMediaFilterValues,
        payload
      );

      state.media.filters.fields = payload;
    },
    updateAppointmentInHistory: (state, { payload }) => {
      state.appointmentHistory.list = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.getDancerInfo.pending, (state) => {
        state.loadingDancerInfoStatus = status.PENDING;
      })
      .addCase(thunks.getDancerInfo.fulfilled, (state, { payload }) => {
        state.dancerInfo = dancerDefaultValuesModel(payload.data);
        state.relationships = payload.data.relationships.map((r) => ({
          ...relativeEditModel({
            ...r,
            roles: relativesRoles,
            id: { value: r.id, label: r.full_name },
            _id: uuid(),
          }),
          status: 'saved',
        }));
        state.loadingDancerInfoStatus = status.SUCCESS;
      })
      .addCase(thunks.getDancerInfo.rejected, (state) => {
        state.loadingDancerInfoStatus = status.FAIL;
      })

      .addCase(thunks.getDancerUpcomingAppointments.pending, (state) => {
        state.loadingAppointmentsStatus = status.PENDING;
      })
      .addCase(
        thunks.getDancerUpcomingAppointments.fulfilled,
        (state, action) => {
          state.dancerUpcomingAppointments = action.payload.data;
          state.loadingAppointmentsStatus = status.SUCCESS;
        }
      )
      .addCase(thunks.getDancerUpcomingAppointments.rejected, (state) => {
        state.loadingAppointmentsStatus = status.FAIL;
      })

      .addCase(thunks.createGuardian.pending, (state) => {
        state.createGuardianStatus = status.PENDING;
      })
      .addCase(thunks.createGuardian.fulfilled, (state, { payload }) => {
        const guardianIndex = state.relationships.findIndex(
          (g) => g._id === payload._id
        );
        if (guardianIndex !== -1) {
          state.relationships[guardianIndex] = {
            ...state.relationships[guardianIndex],
            ...createdRelativeResponseModel({
              ...payload.relative,
              roles: relativesRoles,
            }),
            status: 'saved',
          };
        }
        state.createGuardianStatus = status.SUCCESS;
      })
      .addCase(thunks.createGuardian.rejected, (state) => {
        state.createGuardianStatus = status.FAIL;
      })

      .addCase(thunks.getDancerEvaluationInfo.pending, (state) => {
        state.getEvaluationStatus = status.PENDING;
      })
      .addCase(
        thunks.getDancerEvaluationInfo.fulfilled,
        (state, { payload }) => {
          state.dancerEvaluation = payload.data;
          state.getEvaluationStatus = status.SUCCESS;
        }
      )
      .addCase(thunks.getDancerEvaluationInfo.rejected, (state) => {
        state.getEvaluationStatus = status.FAIL;
      })

      .addCase(thunks.getRatings.pending, (state) => {
        state.loadingRatingStatus = status.PENDING;
      })
      .addCase(thunks.getRatings.fulfilled, (state, { payload }) => {
        state.ratings = payload.data;
        state.loadingRatingStatus = status.SUCCESS;
      })
      .addCase(thunks.getRatings.rejected, (state) => {
        state.loadingRatingStatus = status.FAIL;
      })

      .addCase(thunks.getPerformances.pending, (state) => {
        state.performances.loadingPerformanceStatus = status.PENDING;
      })
      .addCase(thunks.getPerformances.fulfilled, (state, { payload }) => {
        const preparedData = payload?.data?.map((p) => ({
          routine: p.routine_title,
          event: p.event_name,
          dateTime: getPerformanceDateTime(
            p.userLocalDateFormatted,
            p.userLocalTimeFormatted
          ),
          ...p,
        }));

        state.performances.dancerPerformances = preparedData || [];
        state.performances.loadingPerformanceStatus = status.SUCCESS;
      })
      .addCase(thunks.getPerformances.rejected, (state) => {
        state.performances.loadingPerformanceStatus = status.FAIL;
      })

      .addCase(thunks.getNotifications.pending, (state) => {
        state.loadingNotificationsStatus = status.PENDING;
      })
      .addCase(thunks.getNotifications.fulfilled, (state, { payload }) => {
        state.notifications.list = payload.data;
        state.loadingNotificationsStatus = status.SUCCESS;
      })
      .addCase(thunks.getNotifications.rejected, (state) => {
        state.loadingNotificationsStatus = status.FAIL;
      })

      .addCase(thunks.getDancerClasses.pending, (state) => {
        state.loadingClassesStatus = status.PENDING;
      })
      .addCase(thunks.getDancerClasses.fulfilled, (state, { payload }) => {
        state.loadingClassesStatus = status.SUCCESS;
        state.dancerClasses = payload.data;
      })
      .addCase(thunks.getDancerClasses.rejected, (state) => {
        state.loadingClassesStatus = status.FAIL;
      })

      .addCase(thunks.getDancerRoutines.pending, (state) => {
        state.routines.loadingDancerRoutinesStatus = status.PENDING;
      })
      .addCase(thunks.getDancerRoutines.fulfilled, (state, { payload }) => {
        const preparedData = payload.data.map(
          ({ styles, teachers, types, ...rest }) => ({
            ...rest,
            styles: styles?.map((s) => s.name).join(', '),
            teachers: teachers?.map((s) => s.full_name).join(', '),
          })
        );

        state.routines.list = preparedData;
        state.routines.loadingDancerRoutinesStatus = status.SUCCESS;
      })
      .addCase(thunks.getDancerRoutines.rejected, (state) => {
        state.routines.loadingDancerRoutinesStatus = status.FAIL;
      })

      .addCase(thunks.getMediaList.pending, (state) => {
        state.media.loadingMediaListStatus = status.PENDING;
      })
      .addCase(thunks.getMediaList.fulfilled, (state, { payload }) => {
        state.media.loadingMediaListStatus = status.SUCCESS;

        state.media.pagination.total = payload.totalFilteredCount;
        state.media.pagination.uploadedCount = payload.limitedCount;

        state.media.list = payload.data;
      })
      .addCase(thunks.getMediaList.rejected, (state) => {
        state.media.loadingMediaListStatus = status.FAIL;
      })

      .addCase(thunks.getAppointmentHistory.pending, (state) => {
        state.appointmentHistory.loadingStatus = status.PENDING;
      })
      .addCase(thunks.getAppointmentHistory.fulfilled, (state, { payload }) => {
        const formattedData = (payload.data || []).map((appointment) => ({
          ...appointment,
          tableDate: appointment.userLocalDateFormatted || appointment.date,
          time: appointment.userLocalTimeFormatted || appointment.time,
        }));

        // If loadMore is true, append to existing list, otherwise replace
        if (payload.loadMore) {
          state.appointmentHistory.list = [
            ...state.appointmentHistory.list,
            ...formattedData,
          ];
        } else {
          state.appointmentHistory.list = formattedData;
        }

        // Update hasMore and oldestDate
        state.appointmentHistory.hasMore = formattedData.length > 0;
        if (formattedData.length > 0) {
          const lastAppointment = formattedData[formattedData.length - 1];
          state.appointmentHistory.oldestDate =
            lastAppointment.date || lastAppointment.tableDate;
        }

        state.appointmentHistory.loadingStatus = status.SUCCESS;
      })
      .addCase(thunks.getAppointmentHistory.rejected, (state) => {
        state.appointmentHistory.loadingStatus = status.FAIL;
      });
  },
});

const dancerDetails = {
  actions: slice.actions,
  thunks,
  selectors,
};

export { dancerDetails };
export default slice.reducer;
